import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { getProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { getKibbleCurrent, markPerspectiveOpened, type KibblePerspective } from "~/api/kibble"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { HeroPortraitFrame } from "~/components/HeroPortraitFrame"
import { PageShell } from "~/components/PageShell"
import { assetUrl } from "~/utils/assetUrl"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"
import { ChevronDown, PawPrint, Star } from "lucide-react"

export function PerspectivesPage() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [perspective, setPerspective] = useState<KibblePerspective | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([getProfile(), getKibbleCurrent()])
      .then(([profileData, kibbleData]) => {
        setProfile(profileData)
        setPerspective(kibbleData.perspective)
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  useEffect(() => {
    if (!perspective || perspective.opened_at) return
    void markPerspectiveOpened(perspective.delivery_id).catch(() => {})
  }, [perspective])

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  if (!profile) return null

  const dog = profile.dog_profile
  const dogName = dog?.name ?? "Your dog"
  const archetypeName = dog?.archetype_label ?? perspective?.archetype_label ?? "Archetype"
  const archetypeIcon = dog?.archetype_icon
  const heroImage = heroPortraitUrl(archetypeIcon, dog?.portrait)
    ?? (archetypeIcon ? assetUrl(`/images/archetypes/portraits/${archetypeIcon}`) : null)

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <div className="flex justify-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)]"
          >
            <PawPrint size={14} />
            {dogName}
            <ChevronDown size={14} />
          </button>
        </div>

        <div className="mt-4 flex flex-col items-center">
          {archetypeIcon ? (
            <img
              src={assetUrl(`/images/archetypes/logos/${archetypeIcon}`)}
              alt={archetypeName}
              className="h-16 w-16 object-contain"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[var(--color-divider)]">
              <Star size={22} className="text-[var(--color-text-secondary)]" />
            </div>
          )}
          <p className="mt-2 text-center text-4xl font-semibold text-[var(--color-text-primary)]">{dogName}</p>
          <p className="text-center text-xl text-[var(--color-text-secondary)]">{archetypeName}</p>
        </div>

        {heroImage && (
          <HeroPortraitFrame
            className="mt-4"
            src={heroImage}
            alt={`${archetypeName} hero`}
          />
        )}

        {!perspective ? (
          <section className="mt-6 text-center">
            <h1 className="text-2xl font-medium text-[var(--color-text-primary)]">No perspective yet</h1>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              Your first perspective will appear here after your next scheduled delivery.
            </p>
          </section>
        ) : (
          <>
            <section className="mt-4 text-center">
              <h1 className="text-3xl font-medium text-[var(--color-text-primary)]">{archetypeName} Perspective</h1>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">Updated this week</p>
            </section>

            <article className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-5">
              {perspective.title && (
                <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">{perspective.title}</h2>
              )}
              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-[var(--color-text-secondary)]">
                {perspective.body}
              </p>
            </article>
          </>
        )}

        <div className="mt-5 text-center">
          <Link
            to="/dashboard"
            className="inline-flex rounded-full border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-6 py-2 text-sm font-medium text-[var(--color-text-primary)]"
          >
            Back to Dashboard
          </Link>
        </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
